// Scoped router locations isolate tabs; short URLs resolve through this tab's last selected org/project.
import { useParams } from "@tanstack/react-router";

export interface ConsoleScope {
  org: string;
  project: string;
}

const KEY = "nodus.lastScope.v1";

export function useScope(): { org?: string; project?: string } {
  const p = useParams({ strict: false }) as { org?: string; project?: string };
  return { ...(p.org ? { org: p.org } : {}), ...(p.project ? { project: p.project } : {}) };
}

/** The scope a page inside `/$org/$project` renders for; the layout guarantees both are present. */
export function useProjectScope(): ConsoleScope {
  const s = useScope();
  return { org: s.org ?? "", project: s.project ?? "default" };
}

/** The project to show when `wanted` is not one of the org's: its "default" project, else its first. */
export function projectIn(projects: readonly string[], wanted?: string): string {
  if (wanted && projects.includes(wanted)) return wanted;
  return projects.includes("default") ? "default" : (projects[0] ?? "default");
}

export function rememberScope(userId: string, org: string, project?: string) {
  try {
    const all = JSON.parse(sessionStorage.getItem(KEY) ?? localStorage.getItem(KEY) ?? "{}") as Record<string, { org: string; projects: Record<string, string> }>;
    const prev = all[userId];
    const projects = { ...(prev?.projects ?? {}), ...(project ? { [org]: project } : {}) };
    all[userId] = { org, projects };
    localStorage.setItem(KEY, JSON.stringify(all));
    sessionStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Storage disabled: `/` falls back to the first org.
  }
}

export function lastScope(userId: string): { org?: string; project: (org: string) => string } {
  try {
    const all = JSON.parse(sessionStorage.getItem(KEY) ?? localStorage.getItem(KEY) ?? "{}") as Record<string, { org: string; projects: Record<string, string> }>;
    const mine = all[userId];
    return { ...(mine ? { org: mine.org } : {}), project: (org) => mine?.projects[org] ?? "default" };
  } catch {
    return { project: () => "default" };
  }
}
